import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { Popover, PopoverInteractionKind } from '@blueprintjs/core';

import { Box, Flex, Icon, Text } from 'core/components';

const EllipsisIcon = styled(Icon)`
  flex-shrink: 0;
  margin-left: 4px;
  width: 12px;
  min-width: 12px;
`;

const EllipsisBox = ({ children, title, textAlign }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.name === 'dark';

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const { scrollWidth, clientWidth } = textRef.current;
        // Safari requires small tolerance (2px) to prevent oscillation when widths are very close
        const truncated = scrollWidth > clientWidth + 2;
        setIsTruncated((prev) => (prev !== truncated ? truncated : prev));
      }
    };

    let rafId;
    const resizeObserver = new ResizeObserver(() => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(checkTruncation);
    });

    if (textRef.current) {
      resizeObserver.observe(textRef.current);
      checkTruncation();
    }

    return () => {
      resizeObserver.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [children]);

  return (
    <Flex alignItems="center" overflow="hidden" minWidth={0}>
      <Text ref={textRef} as="div" minWidth={0} textAlign={textAlign} title={isTruncated ? undefined : title} ellipsis>
        {children}
      </Text>
      {isTruncated && (
        <Popover
          content={<Box p={1}>{children}</Box>}
          interactionKind={PopoverInteractionKind.HOVER}
          popoverClassName="cell-truncation-tooltip"
          portalClassName={isDarkMode ? 'bp4-dark' : undefined}
          hoverOpenDelay={100}
          hoverCloseDelay={0}
        >
          <EllipsisIcon aria-label="Show full content" icon="more" iconSize={12} color="muted" />
        </Popover>
      )}
    </Flex>
  );
};

EllipsisBox.displayName = 'EllipsisBox';

EllipsisBox.propTypes = {
  children: PropTypes.node,
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
  title: PropTypes.string
};

EllipsisBox.defaultProps = {
  children: null,
  textAlign: 'left',
  title: ''
};

export default EllipsisBox;
